import React, { lazy } from 'react';
import { createRoutesFromElements, Navigate, Route } from 'react-router-dom';

import ProtectedAlumniRoute from 'components/ProtectedAlumniRoute';
import TribeDetail from 'pages/dashboard/tribe-detail';
import Navbar from '../components/Navbar';
import GlobalNotFound from '../components/GlobalNotFound';
import ProtectedRoute from '../components/ProtectedRoute';

// Dashboard Page
const Profile = lazy(() => import('pages/dashboard/profile'));
const Transaction = lazy(() => import('pages/dashboard/transaction'));
const Program = lazy(() => import('pages/dashboard/program'));
const ProgramDetail = lazy(() => import('pages/dashboard/program/detail'));
const Notification = lazy(() => import('pages/dashboard/notification'));
const ECertificate = lazy(() => import('pages/dashboard/e-certificate'));
const Quiz = lazy(() => import('pages/dashboard/quiz'));
const QuizResult = lazy(() => import('pages/dashboard/quiz/result'));
const Document = lazy(() => import('pages/dashboard/document'));
const ExamTest = lazy(() => import('pages/dashboard/exam/ExamTest'));
const ExamResult = lazy(() => import('pages/dashboard/exam/ExamResult'));
const Tribe = lazy(() => import('pages/dashboard/tribe'));
const MyActivities = lazy(() => import('pages/dashboard/my-activities'));
const EvaluationForm = lazy(() => import('pages/dashboard/evaluation-form'));
const AlumniProfile = lazy(() => import('pages/dashboard/alumni-profile'));
const JobPosts = lazy(() => import('pages/dashboard/job-posts'));
const JobPostsDetail = lazy(() => import('pages/dashboard/job-posts-detail'));
const MyJobs = lazy(() => import('pages/dashboard/my-jobs'));
const MyJobsDetail = lazy(() => import('pages/dashboard/my-jobs-detail'));
const PostJobs = lazy(() => import('pages/dashboard/post-jobs'));
const PostJobsDetail = lazy(() => import('pages/dashboard/post-jobs-detail'));
const ApplicantDetail = lazy(() => import('pages/dashboard/applicant-detail'));
const JobPostHeaderDetail = lazy(() =>
  import('pages/dashboard/post-job-header-detail'),
);
const Networking = lazy(() => import('pages/dashboard/networking'));
const NetworkingDetail = lazy(() =>
  import('pages/dashboard/networking-detail'),
);

// Student Learning Page
const StudentLearningPage = lazy(() => import('pages/student-learning'));
const OnlineWatchPage = lazy(() =>
  import('pages/student-learning/pages/OnlineWatchPage'),
);
const OnCampusWatchPage = lazy(() =>
  import('pages/student-learning/pages/OnCampusWatchPage'),
);
const PracticeSessionWatchPage = lazy(() =>
  import('pages/student-learning/pages/PracticeSessionWatchPage'),
);
const WorkshopWatchPage = lazy(() =>
  import('pages/student-learning/pages/WorkshopWatchPage'),
);

const ContactUsPage = lazy(() => import('pages/dashboard/contact-us'));
const ReportPage = lazy(() => import('pages/dashboard/report'));

export default createRoutesFromElements(
  <Route element={<Navbar />}>
    <Route path="/" element={<ProtectedRoute />}>
      {/* Default Redirect to Program */}
      <Route index element={<Navigate to="program" replace />} />

      {/* Program Routes */}
      <Route path="program">
        <Route index element={<Program />} />
        <Route path=":method/:programId" element={<ProgramDetail />} />
      </Route>

      {/* Transaction Route */}
      <Route path="transactions" element={<Transaction />} />

      {/* Profile Route */}
      <Route path="profile" element={<Profile />} />

      {/* Notification Route */}
      <Route path="notification" element={<Notification />} />

      {/* E-Certificate Routes */}
      <Route path="e-certificate" element={<ECertificate />} />
      <Route path="e-certificate/quiz/:certificateId" element={<Quiz />} />
      <Route
        path="e-certificate/quiz/:certificateId/result"
        element={<QuizResult />}
      />

      {/* Document Route */}
      <Route path="document" element={<Document />} />

      {/* Evaluation Form Route */}
      <Route path="evaluation/:evaluationId" element={<EvaluationForm />} />

      <Route element={<ProtectedAlumniRoute />}>
        {/* Tribe Routes */}
        <Route path="tribe">
          <Route index element={<Tribe />} />
          <Route path=":tribeId" element={<TribeDetail />} />
        </Route>

        {/* My Activities Route */}
        <Route path="my-activities" element={<MyActivities />} />

        {/* Alumni Profile Route */}
        <Route path="alumni-profile" element={<AlumniProfile />} />

        {/* Job Posts Routes */}
        <Route path="job-posts">
          <Route index element={<JobPosts />} />
          <Route path=":jobPostId" element={<JobPostsDetail />} />

          {/* My Jobs Routes */}
          <Route path="my-jobs">
            <Route index element={<MyJobs />} />
            <Route path=":jobPostApplicantId" element={<MyJobsDetail />} />
          </Route>

          {/* Post Jobs Routes */}
          <Route path="post-jobs">
            <Route index element={<PostJobs />} />
            <Route path=":jobPostId" element={<PostJobsDetail />} />
            <Route
              path=":jobPostId/applicant/:jobPostApplicantId"
              element={<ApplicantDetail />}
            />
            <Route
              path=":jobPostId/detail/:jobPostId"
              element={<JobPostHeaderDetail />}
            />
          </Route>
        </Route>

        {/* Networking Routes */}
        <Route path="networking">
          <Route index element={<Networking />} />
          <Route path=":slug" element={<NetworkingDetail />} />
        </Route>
      </Route>

      {/* Student Learning Page Routes */}
      <Route path="student-learning" element={<StudentLearningPage />}>
        <Route
          path="video/:programId/topic/:topicId"
          element={<OnlineWatchPage />}
        />
        <Route
          path="practice/:programId/:type/:groupId/:practiceSessionScheduleId"
          element={<PracticeSessionWatchPage />}
        />
        <Route
          path="workshop/:slug/topic/:topicId"
          element={<WorkshopWatchPage />}
        />
        <Route
          path="on-campus/:programId/:onCampusId/session/:sessionId"
          element={<OnCampusWatchPage />}
        />
      </Route>

      {/* Exam Routes */}
      <Route path="exam">
        <Route path=":examTestId" element={<ExamTest />} />
      </Route>
      <Route path="exam-result">
        <Route path=":examTestId" element={<ExamResult />} />
      </Route>

      {/* Contact Us Routes */}
      <Route path="contact-us" element={<ContactUsPage />} />

      {/* Report Routes */}
      <Route path="report" element={<ReportPage />} />
    </Route>

    {/* Fallback Not found Jangan dipindah */}
    <Route path="not-found" element={<GlobalNotFound />} />
    <Route path="*" element={<Navigate to="not-found" replace />} />
  </Route>,
);
